/**
 * データの取り込み。**取り込み元と外部接続先を 1 枚に並べる**（FR29）。
 *
 * ## つないでいないものを、0 件に見せない
 *
 * - 取り込みの口が無い取り込み元は「まだ接続していません」と書く。0 件とは書かない
 * - 口はあるが数えていないものも、件数の代わりに理由を書く
 * - 件数の但し書き（何を数えた数なのか）は、数字と同じ場所に置く
 *
 * ## 置いていない枠
 *
 * - **最終取り込み時刻**。取り込みの実行を記録する台帳がまだ無く、どの取り込み元でも
 *   必ず空になる。理由は画面の上に 1 度だけ書く
 * - **つなぐ・切る・取り込みを起こすボタン**。押しても外側（取り込みの設定や
 *   外部の API）へは届かないので、押せる形そのものを作らない
 */

import { useQuery } from "urql";
import {
  Card,
  Disclosure,
  ErrorState,
  IngestSourcesDocument,
  LoadingState,
  PageHeader,
  Tag,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

/** 取り込みの入り口の形。内部の値そのものは画面に出さない。 */
const CHANNEL_LABELS: Record<string, string> = {
  EXPORT: "定期のエクスポート",
  FILE_HANDOFF: "ファイルの受け渡し",
  PRODUCT_FEED: "商品フィード",
  API: "外部の API",
};

/** 取り込みの実行の記録が無いことは、画面の上で 1 度だけ断る。 */
const NO_RUN_LEDGER_NOTE =
  "最後に取り込めた時刻は出していません。取り込みの実行を記録する台帳がまだ無く、" +
  "どの取り込み元でも空になるためです。代わりに、いま手元に何件あるかだけを出しています。";

function channelLabel(channel: string): string {
  return CHANNEL_LABELS[channel] ?? channel;
}

function formatCount(records: number | null | undefined): string | null {
  if (records === null || records === undefined) return null;
  return `${records.toLocaleString("ja-JP")} 件`;
}

interface SourceRow {
  readonly id: string;
  readonly name: string;
  readonly summary: string;
  readonly tier: string;
  readonly channel: string;
  readonly state: string;
  readonly records: number | null;
  readonly recordsNote: string | null;
  readonly note: string;
  readonly experimental: boolean;
  readonly fills: readonly {
    readonly screen: string;
    readonly target: string;
    readonly route: string;
  }[];
  readonly missingRequiredFields:
    | readonly {
        readonly field: string;
        readonly label: string;
        readonly count: number;
      }[]
    | null;
  readonly dataGap: {
    readonly what: string;
    readonly why: string;
    readonly needs: string;
  } | null;
}

function StateTag({ state }: { state: string }) {
  return state === "CONNECTED" ? (
    <Tag tone="ok">取り込めています</Tag>
  ) : (
    <Tag>まだ接続していません</Tag>
  );
}

function SourceCard({ source }: { source: SourceRow }) {
  const count = formatCount(source.records);
  const missing = source.missingRequiredFields ?? [];
  const missingWithRows = missing.filter((row) => row.count > 0);

  return (
    <section
      aria-label={source.name}
      style={{ borderTop: "1px solid var(--vd-border)", paddingTop: 12 }}
    >
      <div className="vd-row" style={{ justifyContent: "space-between" }}>
        <div className="vd-card-title">
          {source.name}
          <StateTag state={source.state} />
          {source.experimental ? <Tag tone="info">実験的な接続</Tag> : null}
        </div>
        <div className="vd-stat-note">{channelLabel(source.channel)}</div>
      </div>

      <p style={{ margin: "4px 0 0" }}>{source.summary}</p>

      <div className="vd-row" style={{ marginTop: 8, alignItems: "baseline" }}>
        {count === null ? (
          <strong aria-label={`${source.name}の件数`}>数えていません</strong>
        ) : (
          <strong aria-label={`${source.name}の件数`}>{count}</strong>
        )}
        <span className="vd-stat-note">
          {count === null
            ? (source.dataGap?.why ?? "理由が分かりません。")
            : (source.recordsNote ?? "")}
        </span>
      </div>

      {missingWithRows.length === 0 ? null : (
        <p className="vd-stat-note" style={{ margin: "8px 0 0" }}>
          取り込めてはいるものの、欠けている項目があります:{" "}
          {missingWithRows
            .map(
              (row) =>
                `${row.label} ${row.count.toLocaleString("ja-JP")} 件ぶん`,
            )
            .join(" / ")}
        </p>
      )}

      {missing.length > 0 && missingWithRows.length === 0 ? (
        <p className="vd-stat-note" style={{ margin: "8px 0 0" }}>
          必須の項目は、数えた範囲では欠けていません。
        </p>
      ) : null}

      <p className="vd-stat-note" style={{ margin: "8px 0 0" }}>
        {source.note}
      </p>

      {source.fills.length === 0 ? null : (
        <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
          つながると埋まるもの:{" "}
          {source.fills
            .map((fill) => `${fill.screen} の ${fill.target}`)
            .join(" / ")}
        </p>
      )}
    </section>
  );
}

export function IntakePage() {
  const [result, refetch] = useQuery({ query: IngestSourcesDocument });

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const all = result.data.ingestSources as readonly SourceRow[];
  const required = all.filter((source) => source.tier === "REQUIRED");
  const later = all.filter((source) => source.tier === "LATER");
  const experimental = all.filter((source) => source.tier === "EXPERIMENTAL");

  return (
    <div className="vd-page">
      <PageHeader
        title="データの取り込み"
        description="商品・投稿・反応・レビューがどこから入っているかです。まだつないでいないものも、同じ一覧に並べます。"
        titleExtra={<HelpIcon slug="intake/sources" />}
      />

      <div className="vd-stack">
        <Card
          title="10 月に必要なもの"
          note={NO_RUN_LEDGER_NOTE}
          extra={<HelpIcon slug="intake/sources" />}
        >
          <div className="vd-stack">
            {required.map((source) => (
              <SourceCard key={source.id} source={source} />
            ))}
          </div>
        </Card>

        {experimental.length === 0 ? null : (
          <Card
            title="実験的な接続"
            extra={<HelpIcon slug="intake/sources" />}
            note="つながりを確かめるためのものです。有効にしてあっても、取り込みにはまだ効きません。"
          >
            <div className="vd-stack">
              {experimental.map((source) => (
                <SourceCard key={source.id} source={source} />
              ))}
            </div>
          </Card>
        )}

        {later.length === 0 ? null : (
          <Card
            title="このあと足すもの"
            extra={<HelpIcon slug="intake/sources" />}
          >
            <Disclosure summary="いまは外しているもの">
              <div className="vd-stack">
                {later.map((source) => (
                  <SourceCard key={source.id} source={source} />
                ))}
              </div>
            </Disclosure>
          </Card>
        )}
      </div>
    </div>
  );
}
